import * as THREE from 'three';
import type { CameraCue } from '../story/episode';

const ease = (value: number): number => 1 - Math.pow(1 - value, 3);

export class CinematicCamera {
  private readonly target = new THREE.Vector3();
  private readonly startTarget = new THREE.Vector3();
  private readonly endTarget = new THREE.Vector3();
  private readonly startPosition = new THREE.Vector3();
  private readonly endPosition = new THREE.Vector3();
  private startFov = 32;
  private endFov = 32;
  private blend = 1;
  private blendDuration = 0.85;
  private cue?: CameraCue;

  constructor(private readonly camera: THREE.PerspectiveCamera) {}

  setCue(cue: CameraCue, immediate = false): void {
    this.cue = cue;
    this.startPosition.copy(this.camera.position);
    this.startTarget.copy(this.target);
    this.startFov = this.camera.fov;
    this.endPosition.fromArray(cue.position);
    this.endTarget.fromArray(cue.target);
    this.endFov = cue.fov;
    this.blend = immediate ? 1 : 0;
    if (immediate) this.apply(1, 0);
  }

  update(delta: number, timelineTime: number, reducedMotion = false): void {
    if (this.blend < 1) this.blend = Math.min(1, this.blend + delta / this.blendDuration);
    this.apply(ease(this.blend), reducedMotion ? 0 : timelineTime);
  }

  nudgePosition(axis: 'x' | 'y' | 'z', value: number): void {
    this.endPosition[axis] = value;
    if (this.cue) this.cue.position = this.endPosition.toArray() as [number, number, number];
  }

  setFov(value: number): void {
    this.endFov = THREE.MathUtils.clamp(value, 18, 65);
  }

  getTarget(): THREE.Vector3 {
    return this.target.clone();
  }

  private apply(progress: number, timelineTime: number): void {
    this.camera.position.lerpVectors(this.startPosition, this.endPosition, progress);
    this.target.lerpVectors(this.startTarget, this.endTarget, progress);
    this.camera.fov = THREE.MathUtils.lerp(this.startFov, this.endFov, progress);

    if (this.cue && this.blend >= 1) {
      const t = timelineTime;
      if (this.cue.preset === 'orbit') {
        const radius = this.camera.position.distanceTo(this.target);
        const angle = t * 0.045;
        this.camera.position.x = this.endPosition.x + Math.sin(angle) * radius * 0.11;
        this.camera.position.z = this.endPosition.z + (Math.cos(angle) - 1) * radius * 0.08;
      } else if (this.cue.preset === 'follow' || this.cue.preset === 'low') {
        this.camera.position.y += Math.sin(t * 2.4) * 0.025;
      } else if (this.cue.preset === 'crane') {
        this.camera.position.y += Math.sin(t * 0.32) * 0.18;
      }
    }

    this.camera.lookAt(this.target);
    this.camera.updateProjectionMatrix();
  }
}
